import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiTypography from "components/base/UiTypography";
import {
  AdditionalCostFeeType,
  EquipmentPieceChargeType,
  ProductLabourCostType,
  ProposalAdditionalCost,
  ProposalEquipmentPiece,
  ProposalProduct,
  ProposalStage,
} from "generated/graphql";
import useProposalProductDetails from "hooks/products/useProposalProductDetails";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { sentenceCase } from "change-case";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";
import CostSection from "./CostSection";
import { CostRow } from "./CostSection";
import ExpandedWrapper from "./ExpandedWrapper";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import MaterialTruckingSection from "./MaterialTruckingSection";
import StatementOfWork from "./StatementOfWork";
import useMarkupInputProps from "./useMarkupInputProps";
import {
  MATERIAL_COLUMNS,
  EQUIPMENT_COLUMNS,
  LABOR_COLUMNS,
  ADDITIONAL_COST_COLUMNS,
} from "./costColumnDefinitions";

type DefaultSectionProps = {
  product: ProposalProduct;
  stage: ProposalStage;
  onOpenMaterialConfig?: () => void;
  onOpenTruckingConfig?: () => void;
  onAddEquipment?: () => void;
  onAddLabor?: () => void;
  onAddAdditionalCost?: () => void;
  onAddMaterial?: () => void;
  onMeasureJob?: () => void;
  onDeleteEquipment?: (id: string) => void;
  onDeleteLabor?: (id: string) => void;
  onDeleteAdditionalCost?: (id: string) => void;
  onDeleteMaterial?: (id: string) => void;
  onDeletePlantProduct?: (plantProductId: string) => void;
};

function isFullOrHalfDay(chargeType?: string): boolean {
  return (
    chargeType === EquipmentPieceChargeType.FULL_DAY ||
    chargeType === EquipmentPieceChargeType.HALF_DAY
  );
}

function getEquipmentTimeAdornment(chargeType?: string): string {
  if (chargeType === EquipmentPieceChargeType.FULL_DAY) return "day";
  if (chargeType === EquipmentPieceChargeType.HALF_DAY) return "1/2 day";
  return "hr";
}

function isEquipmentTravelDisabled(eq: ProposalEquipmentPiece): boolean {
  if (isFullOrHalfDay(eq.chargeType)) return true;
  return !eq.calculateTravelTime;
}

function getLaborTimeAdornment(labourCostType?: string): string {
  if (labourCostType === ProductLabourCostType.FULL_DAY) return "day";
  if (labourCostType === ProductLabourCostType.HALF_DAY) return "1/2 day";
  return "hr";
}

function secsToHrs(secs?: number | null): string {
  if (secs == null) return "";
  return (secs / 3600).toFixed(2);
}

function overrideOrEmpty(
  override: number | null | undefined,
  formatter: (v: number) => string
): string {
  return override != null ? formatter(override) : "";
}

function getEquipmentTravelPlaceholder(eq: ProposalEquipmentPiece): string {
  if (isFullOrHalfDay(eq.chargeType)) return "0";
  return secsToHrs(eq.timeTravel) || "0";
}

function getEquipmentJobTimePlaceholder(
  eq: ProposalEquipmentPiece,
  jobDurationInDays: number,
  averageWorkingHoursPerDay: number
): string {
  if (eq.chargeType === EquipmentPieceChargeType.FULL_DAY) {
    return Math.ceil(jobDurationInDays).toFixed(2);
  }
  if (eq.chargeType === EquipmentPieceChargeType.HALF_DAY) {
    return Math.ceil(jobDurationInDays * 2).toFixed(2);
  }
  const useRate = (eq.usePercentage ?? 100) / 100;
  if (eq.timeOnjob) return ((eq.timeOnjob / 3600) * useRate).toFixed(2);
  return (jobDurationInDays * averageWorkingHoursPerDay * useRate).toFixed(2);
}

function getLaborJobTimePlaceholder(
  crew: { timeOnjob?: number | null },
  labourCostType: string | undefined,
  isPerManHour: boolean,
  jobDurationInDays: number,
  averageWorkingHoursPerDay: number
): string {
  if (labourCostType === ProductLabourCostType.FULL_DAY) {
    return Math.ceil(jobDurationInDays).toFixed(2);
  }
  if (labourCostType === ProductLabourCostType.HALF_DAY) {
    return Math.ceil(jobDurationInDays * 2).toFixed(2);
  }
  if (isPerManHour) {
    return crew.timeOnjob
      ? (crew.timeOnjob / 3600).toFixed(2)
      : (jobDurationInDays * averageWorkingHoursPerDay).toFixed(2);
  }
  return crew.timeOnjob ? (crew.timeOnjob / 3600).toFixed(2) : jobDurationInDays.toFixed(2);
}

type FieldParser = (value: string) => string | number | null;

const toFloat: FieldParser = (v) => {
  if (v === "") return null;
  const parsed = parseFloat(v);
  return Number.isNaN(parsed) ? null : parsed;
};
const toInt: FieldParser = (v) => (v ? parseInt(v, 10) : null);
const toCents: FieldParser = (v) => Math.round(parseFloat(v) * 100);
const toFloatOrZero: FieldParser = (v) => parseFloat(v) || 0;
const identity: FieldParser = (v) => v;

type FieldMapping = { formKey: string; parse: FieldParser };

const EQUIP_FIELDS: Record<string, FieldMapping> = {
  quantity: { formKey: "quantity", parse: toFloat },
  rate: { formKey: "rate", parse: toCents },
  travelTime: { formKey: "timeTravel", parse: toFloat },
  jobTime: { formKey: "timeOnJob", parse: toFloat },
};

const CREW_FIELDS: Record<string, FieldMapping> = {
  quantity: { formKey: "quantity", parse: toFloat },
  rate: { formKey: "rate", parse: toCents },
  travelTime: { formKey: "timeTravel", parse: toFloat },
  jobTime: { formKey: "timeOnjob", parse: toFloat },
};

const AC_FIELDS: Record<string, FieldMapping> = {
  percentOrFee: { formKey: "feeAmount", parse: toFloatOrZero },
  quantity: { formKey: "quantity", parse: toFloatOrZero },
  rate: { formKey: "cost", parse: toCents },
  cost: { formKey: "cost", parse: toCents },
};

const MAT_FIELDS: Record<string, FieldMapping> = {
  materialName: { formKey: "name", parse: identity },
  percentage: { formKey: "percentage", parse: toFloatOrZero },
  quantity: { formKey: "quantityOverride", parse: toInt },
  rate: { formKey: "rate", parse: toCents },
};

export default function DefaultSection({
  product,
  stage,
  onOpenMaterialConfig,
  onOpenTruckingConfig,
  onAddEquipment,
  onAddLabor,
  onAddAdditionalCost,
  onAddMaterial,
  onMeasureJob,
  onDeleteEquipment,
  onDeleteLabor,
  onDeleteAdditionalCost,
  onDeleteMaterial,
  onDeletePlantProduct,
}: DefaultSectionProps) {
  const {
    register,
    setValue: setFormValue,
    getValues,
    control,
  } = useFormContext<UpdateProposalProductInputSchema>();
  const { requiresTrucking } = useProposalProductDetails(product);
  const { organizationSettings } = useContext(ProposalFormsContext);
  const averageWorkingHoursPerDay = +(organizationSettings?.["average_working_hours_per_day"] ?? 0);

  const jobDuration = product.jobDurationOverride || product.jobDuration || 0;
  const jobDurationInDays = averageWorkingHoursPerDay ? jobDuration / averageWorkingHoursPerDay : 0;
  const daysPlaceholder =
    jobDuration > 0 ? (jobDuration / (averageWorkingHoursPerDay || 1)).toFixed(1) : "0";
  const hoursPlaceholder = jobDuration > 0 ? jobDuration.toFixed(2) : "0";
  const travelTimePlaceholder =
    product.roundTripTravelTimeHours ??
    (stage?.distanceSeconds ? ((stage.distanceSeconds / 3600) * 2).toFixed(2) : "0");

  const [deletedEquipIds, setDeletedEquipIds] = useState<Set<string>>(() => new Set());
  const [deletedCrewIds, setDeletedCrewIds] = useState<Set<string>>(() => new Set());
  const [deletedACIds, setDeletedACIds] = useState<Set<string>>(() => new Set());

  const hasPlantProducts = (product.proposalPlantProducts ?? []).length > 0;
  const materialTotal = requiresTrucking
    ? formatCentsToUSD(+product.costMaterial + +product.costTransferTruck)
    : formatCentsToUSD(+product.costMaterial);

  const labourCostType = product.material?.labourCostType;
  const isPerManHour = labourCostType === ProductLabourCostType.PER_MAN_HOUR;
  const laborJobTimeLabel = "Job Time";
  const laborTimeAdornment = getLaborTimeAdornment(labourCostType);
  const isLaborJobTimeDisabled =
    !isPerManHour &&
    labourCostType !== ProductLabourCostType.FULL_DAY &&
    labourCostType !== ProductLabourCostType.HALF_DAY;

  const watchedDaysOverride = useWatch({ control, name: "jobDurationDaysOverride" });
  const watchedHoursOverride = useWatch({ control, name: "jobDurationOverride" });
  const hasDaysOverride = !!watchedDaysOverride;
  const hasHoursOverride = !!watchedHoursOverride;
  const markupInputProps = useMarkupInputProps({
    register,
    setValue: setFormValue,
  });

  const profitFields = [
    {
      label: "Profit Markup %",
      name: "material.markup" as const,
      placeholder:
        product.material.markupPercentageOverride ?? product.material.markupPercentage ?? "",
      disabled: false,
      inputProps: markupInputProps,
    },
    {
      label: "Production rate",
      name: "material.dailyUnitsComplete" as const,
      placeholder: product.material.dailyUnitsComplete,
      disabled: hasDaysOverride || hasHoursOverride,
    },
    {
      label: "Days",
      name: "jobDurationDaysOverride" as const,
      placeholder: daysPlaceholder,
      disabled: hasHoursOverride,
    },
    {
      label: "Hours",
      name: "jobDurationOverride" as const,
      placeholder: hoursPlaceholder,
      disabled: hasDaysOverride,
    },
    {
      label: "Travel Time (hrs)",
      name: "roundTripTravelTimeHours" as const,
      placeholder: travelTimePlaceholder,
      disabled: false,
    },
  ];

  const coverageRate = (() => {
    if (requiresTrucking) {
      const pps = product.proposalPlantProducts ?? [];
      const totalMaterialNeeded = pps.reduce(
        (sum, pp) => sum + (pp.materialNeededOverride ?? pp.materialNeeded ?? 0),
        0
      );
      const jobSize = product.jobSize ?? 0;
      if (jobSize > 0 && totalMaterialNeeded > 0) {
        return (totalMaterialNeeded / jobSize).toFixed(2);
      }
      return "";
    }
    return product.material.areaCoverage != null
      ? Number(product.material.areaCoverage).toFixed(2)
      : "";
  })();

  const materialRows: CostRow[] = (product.proposalMaterialNoPlants ?? []).map((m) => {
    const qty = m.quantityOverride ?? m.quantity ?? 0;
    return {
      materialName: m.name,
      percentage: String(m.percentage ?? 100),
      quantity: String(qty),
      rate: (m.rate / 100).toFixed(2),
      cost: ((m.rate * qty) / 100).toFixed(2),
      __values: {
        materialName: m.name,
        percentage: String(m.percentage ?? 100),
        quantity: overrideOrEmpty(m.quantityOverride, String),
        rate: (m.rate / 100).toFixed(2),
      },
      __columnOverrides: {
        rate: { placeholder: "" },
      },
    };
  });

  const equipmentRows: CostRow[] = (product.equipmentPieces ?? []).map((eq) => {
    const travelDisabled = isEquipmentTravelDisabled(eq);
    const timeAdornment = getEquipmentTimeAdornment(eq.chargeType);

    return {
      name: eq.name,
      quantity: String(eq.quantity ?? 1),
      rate: formatCentsToUSD(eq.rate ?? 0),
      travelTime: getEquipmentTravelPlaceholder(eq),
      jobTime: getEquipmentJobTimePlaceholder(eq, jobDurationInDays, averageWorkingHoursPerDay),
      cost: formatCentsToUSD(eq.cost ?? 0),
      __values: {
        quantity: overrideOrEmpty(eq.quantityOverride, String),
        rate: overrideOrEmpty(eq.rateOverride, formatCentsToUSD),
        travelTime: secsToHrs(eq.timeTravelOverride),
        jobTime: secsToHrs(eq.timeOnjobOverride),
      },
      __columnOverrides: {
        travelTime: { endAdornment: "hr", ...(travelDisabled && { readOnly: true }) },
        jobTime: { endAdornment: timeAdornment },
      },
    };
  });

  const crewRows: CostRow[] = (product.crews ?? []).map((c) => {
    const travelDisabled = !isPerManHour;
    const travelPlaceholder = isPerManHour ? secsToHrs(c.timeTravel) || "0" : "0";
    const jobTimePlaceholder = getLaborJobTimePlaceholder(
      c,
      labourCostType,
      isPerManHour,
      jobDurationInDays,
      averageWorkingHoursPerDay
    );

    return {
      name: c.name,
      quantity: String(c.quantity ?? 1),
      rate: formatCentsToUSD(c.rate ?? 0),
      travelTime: travelPlaceholder,
      jobTime: jobTimePlaceholder,
      cost: formatCentsToUSD(c.cost ?? 0),
      __values: {
        quantity: overrideOrEmpty(c.quantityOverride, String),
        rate: overrideOrEmpty(c.rateOverride, formatCentsToUSD),
        travelTime: secsToHrs(c.timeTravelOverride),
        jobTime: secsToHrs(c.timeOnjobOverride),
      },
      __columnOverrides: {
        travelTime: { endAdornment: "hr", ...(travelDisabled && { readOnly: true }) },
        jobTime: {
          endAdornment: laborTimeAdornment,
          ...(isLaborJobTimeDisabled && { readOnly: true }),
        },
      },
    };
  });

  const additionalCostRows: CostRow[] = (product.additionalCosts ?? []).map((ac) => {
    const feeType = ac.feeType as AdditionalCostFeeType | undefined;
    const feeDisabled =
      feeType === AdditionalCostFeeType.JOB_LENGTH || feeType === AdditionalCostFeeType.FLAT;
    const costDisabled = feeType === AdditionalCostFeeType.JOB_COST;

    return {
      name: ac.name,
      costType: ac.feeType ? sentenceCase(ac.feeType) : "Fee",
      percentOrFee: String(ac.feeAmount ?? 0),
      quantity: String(ac.quantity ?? 1),
      rate: (ac.cost / 100).toFixed(2),
      cost: formatCentsToUSD(+(ac as unknown as Record<string, number>).costTotal || 0),
      showOnProposal: ac.showOnProposal ?? false,
      __values: {
        percentOrFee: overrideOrEmpty(ac.feeAmountOverride, String),
        quantity: overrideOrEmpty(ac.quantityOverride, String),
        rate: overrideOrEmpty(ac.costOverride, (v) => (v / 100).toFixed(2)),
      },
      __columnOverrides: {
        ...(feeDisabled && { percentOrFee: { readOnly: true } }),
        ...(costDisabled && { rate: { readOnly: true } }),
      },
    };
  });

  const laborColumns = LABOR_COLUMNS.map((col) => {
    if (col.key === "jobTime") return { ...col, label: laborJobTimeLabel };
    return col;
  });

  const makeFieldHandler = useCallback(
    (prefix: string, fields: Record<string, FieldMapping>) =>
      (rowIndex: number, colKey: string, value: string) => {
        const mapping = fields[colKey];
        if (!mapping) return;
        setFormValue(`${prefix}.${rowIndex}.${mapping.formKey}` as any, mapping.parse(value), {
          shouldDirty: true,
        });
      },
    [setFormValue]
  );

  const handleEquipmentChange = useMemo(
    () => makeFieldHandler("equipmentPieces", EQUIP_FIELDS),
    [makeFieldHandler]
  );
  const handleLaborChange = useMemo(
    () => makeFieldHandler("crews", CREW_FIELDS),
    [makeFieldHandler]
  );
  const handleMaterialChange = useMemo(
    () => makeFieldHandler("materialNoPlants", MAT_FIELDS),
    [makeFieldHandler]
  );
  const handleAdditionalCostChange = useMemo(
    () => makeFieldHandler("additionalCosts", AC_FIELDS),
    [makeFieldHandler]
  );

  const handleAdditionalCostCheckbox = useCallback(
    (rowIndex: number, colKey: string, checked: boolean) => {
      if (colKey === "showOnProposal") {
        setFormValue(`additionalCosts.${rowIndex}.showOnProposal` as any, checked, {
          shouldDirty: true,
        });
      }
    },
    [setFormValue]
  );

  const equipItems = product.equipmentPieces ?? [];
  const crewItems = product.crews ?? [];
  const acItems = product.additionalCosts ?? [];

  useEffect(() => {
    setDeletedEquipIds(new Set());
    setDeletedCrewIds(new Set());
    setDeletedACIds(new Set());
  }, [product.id, equipItems.length, crewItems.length, acItems.length]);

  const visibleEquipmentRows = equipItems.flatMap((item, sourceIndex) => {
    if (deletedEquipIds.has(item.id)) return [];
    const formIndex = (getValues("equipmentPieces") ?? []).findIndex(
      (entry: any) => entry.id === item.id
    );
    if (formIndex < 0) return [];
    return [{ ...equipmentRows[sourceIndex], __rowIndex: formIndex, __rowKey: item.id }];
  });

  const visibleCrewRows = crewItems.flatMap((item, sourceIndex) => {
    if (deletedCrewIds.has(item.id)) return [];
    const formIndex = (getValues("crews") ?? []).findIndex((entry: any) => entry.id === item.id);
    if (formIndex < 0) return [];
    return [{ ...crewRows[sourceIndex], __rowIndex: formIndex, __rowKey: item.id }];
  });

  const visibleAdditionalCostRows = acItems.flatMap((item, sourceIndex) => {
    if (deletedACIds.has(item.id)) return [];
    const formIndex = (getValues("additionalCosts") ?? []).findIndex(
      (entry: any) => entry.id === item.id
    );
    if (formIndex < 0) return [];
    return [{ ...additionalCostRows[sourceIndex], __rowIndex: formIndex, __rowKey: item.id }];
  });

  const visibleMaterialRows = (product.proposalMaterialNoPlants ?? []).flatMap(
    (item, sourceIndex) => {
      const formIndex = (getValues("materialNoPlants") ?? []).findIndex(
        (entry: any) => entry.id === item.id
      );
      if (formIndex < 0) return [];
      return [{ ...materialRows[sourceIndex], __rowIndex: formIndex, __rowKey: item.id }];
    }
  );

  const handleDeleteEquipmentWrapped = useCallback(
    (formIndex: number) => {
      const item = (getValues("equipmentPieces") ?? [])[formIndex];
      if (item) {
        setDeletedEquipIds((prev) => new Set(prev).add(item.id));
        onDeleteEquipment(item.id);
      }
    },
    [getValues, onDeleteEquipment]
  );

  const handleDeleteLaborWrapped = useCallback(
    (formIndex: number) => {
      const item = (getValues("crews") ?? [])[formIndex];
      if (item) {
        setDeletedCrewIds((prev) => new Set(prev).add(item.id));
        onDeleteLabor(item.id);
      }
    },
    [getValues, onDeleteLabor]
  );

  const handleDeleteAdditionalCostWrapped = useCallback(
    (formIndex: number) => {
      const item = (getValues("additionalCosts") ?? [])[formIndex];
      if (item) {
        setDeletedACIds((prev) => new Set(prev).add(item.id));
        onDeleteAdditionalCost(item.id);
      }
    },
    [getValues, onDeleteAdditionalCost]
  );

  const handleDeleteMaterialWrapped = useCallback(
    (formIndex: number) => {
      const item = (getValues("materialNoPlants") ?? [])[formIndex];
      if (item?.id) {
        onDeleteMaterial?.(item.id);
      }
    },
    [getValues, onDeleteMaterial]
  );

  return (
    <ExpandedWrapper>
      <UiCard variant="section">
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            "&::before": { display: "none" },
            "& .MuiAccordionSummary-root": { minHeight: 0, px: "8px", py: "4px" },
            "& .MuiAccordionSummary-content": { m: 0 },
            "& .MuiAccordionDetails-root": { p: 0, pt: "8px" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flex={1}
              mr={1}
            >
              <UiTypography uiVariant="overline">Profit and Production</UiTypography>
              <UiButton
                uiVariant="primary-grey"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onMeasureJob();
                }}
              >
                Measure job
              </UiButton>
            </MDBox>
          </AccordionSummary>
          <AccordionDetails>
            <MDBox px="8px" pb="8px">
              <Grid container spacing="8px">
                {profitFields.map((field) => (
                  <Grid item xs={6} md={2} key={field.label}>
                    <UiFieldGroup
                      label={field.label}
                      value={String(field.placeholder ?? "")}
                      readOnly={field.disabled}
                      inputProps={
                        (field as { inputProps?: Record<string, unknown> }).inputProps ??
                        register(field.name as any, NUMERIC_SET_VALUE_AS)
                      }
                    />
                  </Grid>
                ))}
                <Grid item xs={6} md={2}>
                  <UiFieldGroup label="Coverage Rate" value={coverageRate} readOnly />
                </Grid>
              </Grid>
            </MDBox>
          </AccordionDetails>
        </Accordion>
      </UiCard>

      <StatementOfWork />

      {!hasPlantProducts && (
        <CostSection
          title="Material Costs"
          total={materialTotal}
          actionLabel="Configure material"
          onActionClick={onOpenMaterialConfig}
          columns={MATERIAL_COLUMNS}
          rows={visibleMaterialRows}
          onAdd={onAddMaterial}
          onFieldChange={handleMaterialChange}
          onDelete={handleDeleteMaterialWrapped}
        />
      )}

      {requiresTrucking && (
        <MaterialTruckingSection
          product={product}
          onConfigureTrucking={onOpenTruckingConfig}
          onAddMaterial={onAddMaterial}
          onDeletePlantProduct={onDeletePlantProduct}
          onOpenMaterialConfig={onOpenMaterialConfig}
        />
      )}

      <CostSection
        title="Equipment Costs"
        total={formatCentsToUSD(product.costEquipment)}
        columns={EQUIPMENT_COLUMNS}
        rows={visibleEquipmentRows}
        onAdd={onAddEquipment}
        onFieldChange={handleEquipmentChange}
        onDelete={handleDeleteEquipmentWrapped}
      />

      <CostSection
        title="Labor Costs"
        total={formatCentsToUSD(product.costLabour)}
        columns={laborColumns}
        rows={visibleCrewRows}
        onAdd={onAddLabor}
        onFieldChange={handleLaborChange}
        onDelete={handleDeleteLaborWrapped}
      />

      <CostSection
        title="Additional Costs"
        total={formatCentsToUSD(product.costAdditional)}
        columns={ADDITIONAL_COST_COLUMNS}
        rows={visibleAdditionalCostRows}
        onAdd={onAddAdditionalCost}
        onFieldChange={handleAdditionalCostChange}
        onCheckboxChange={handleAdditionalCostCheckbox}
        onDelete={handleDeleteAdditionalCostWrapped}
      />
    </ExpandedWrapper>
  );
}
